import {
    Button,
    Heading,
    HStack,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FaIcon } from '@acp/core-ui';
import { faCheckCircle, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { useMemo } from 'react';
import { DocumentUploadItem } from './upload-item';
import { DocumentUploadQueueContext, useUploadQueueStats } from './context';

export function DocumentUploadManager() {
    const {
        hasPendingItems,
        failedCount,
        successCount,
        queuedCount,
        activeCount,
    } = useUploadQueueStats();

    const machine = DocumentUploadQueueContext.useActorRef();

    const queue = DocumentUploadQueueContext.useSelector(
        (state) => state.context.queue
    );

    const buttonColor = useMemo(() => {
        if (hasPendingItems) {
            return 'muted.500';
        }

        if (failedCount > 0) {
            return 'danger.500';
        }

        return 'success.500';
    }, [hasPendingItems, failedCount]);

    if (queue.length === 0) {
        return null;
    }

    return (
        <Popover>
            <PopoverTrigger>
                <Button
                    variant="ghost"
                    size="sm"
                    colorScheme="muted"
                    color={buttonColor}
                >
                    {
                        hasPendingItems && (
                            <>
                                <Spinner size="sm" mr={2} />
                                Uploading documents
                                {' '}
                                (
                                {activeCount + queuedCount}
                                )
                            </>
                        )
                    }
                    {
                        !hasPendingItems && failedCount === 0 && (
                            <>
                                <FaIcon w={4} h={4} icon={faCheckCircle} mr={2} />
                                {successCount}
                                {' '}
                                {successCount > 1 ? 'uploads' : 'upload'}
                                {' '}
                                completed
                            </>
                        )
                    }
                    {
                        !hasPendingItems && failedCount > 0 && (
                            <>
                                <FaIcon w={4} h={4} icon={faExclamationCircle} mr={2} />
                                {failedCount}
                                {' '}
                                {failedCount > 1 ? 'uploads' : 'upload'}
                                {' '}
                                failed
                            </>
                        )
                    }
                </Button>
            </PopoverTrigger>
            <PopoverContent w={96}>
                <PopoverArrow />
                <PopoverBody px={0} pb={0} pt={0} w={96}>
                    <HStack alignItems="center" px={5} pt={4} pb={1}>
                        <Heading size="sm">Your uploads</Heading>
                        <Text
                            color="muted.500"
                            textDecoration="none"
                            _hover={{ textDecoration: 'underline' }}
                            cursor="pointer"
                            fontSize="sm"
                            onClick={() => machine.send({ type: 'CLEAR_COMPLETED' })}
                        >
                            Clear
                        </Text>
                    </HStack>
                    <Text fontSize="sm" pb={4} color="muted.500" px={5}>
                        Don&apos;t close this window while uploads are in progress
                    </Text>
                    <VStack
                        gap={0}
                        alignItems="start"
                        maxWidth="100%"
                        maxH="400px"
                        overflowY="auto"
                        borderTopWidth={1}
                        borderColor="muted.50"
                    >
                        {
                            queue.filter((upload) => upload.getSnapshot().matches('uploading')).map((upload) => (
                                <DocumentUploadItem
                                    key={upload.id}
                                    actorRef={upload}
                                    py={3}
                                    width="100%"
                                    borderTopWidth={1}
                                    borderColor="muted.50"
                                    _first={{
                                        borderTopWidth: 0,
                                    }}
                                    px={5}
                                    showAddress
                                />
                            ))
                        }
                        {
                            queue.filter((upload) => upload.getSnapshot().matches('failed')).map((upload) => (
                                <DocumentUploadItem
                                    key={upload.id}
                                    actorRef={upload}
                                    py={3}
                                    width="100%"
                                    borderTopWidth={1}
                                    borderColor="muted.50"
                                    _first={{
                                        borderTopWidth: 0,
                                    }}
                                    px={5}
                                    showAddress
                                />
                            ))
                        }
                        {
                            queue.filter((upload) => upload.getSnapshot().matches('queued')).map((upload) => (
                                <DocumentUploadItem
                                    key={upload.id}
                                    actorRef={upload}
                                    py={3}
                                    width="100%"
                                    borderTopWidth={1}
                                    borderColor="muted.50"
                                    _first={{
                                        borderTopWidth: 0,
                                    }}
                                    px={5}
                                    showAddress
                                />
                            ))
                        }
                        {
                            queue.filter((upload) => upload.getSnapshot().matches('success')).map((upload) => (
                                <DocumentUploadItem
                                    key={upload.id}
                                    actorRef={upload}
                                    py={3}
                                    width="100%"
                                    borderTopWidth={1}
                                    borderColor="muted.50"
                                    _first={{
                                        borderTopWidth: 0,
                                    }}
                                    px={5}
                                    showAddress
                                />
                            ))
                        }
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}
